import React from 'react';
import { styled } from '@mui/material';
import Popper from 'components/interface/Popper/Popper';
import TextField, {
    TextFieldProps,
} from 'components/interface/textField/TextField';
import Symbol from 'components/Symbol';
import { TimePickerColumn } from './TimePickerColumn';
import {
    formatTimeValue,
    ParsedTimeValue,
    parseTimeInput,
    sanitizeTimeInput,
} from './utils';

const ToggleButton = styled('button', {
    shouldForwardProp: prop => prop !== '$disabled',
})<{
    $disabled?: boolean;
}>(({ theme, $disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: 0,
    border: 0,
    background: 'transparent',
    color: 'inherit',
    cursor: $disabled ? 'default' : 'pointer',
}));

const Anchor = styled('div')({
    display: 'block',
    width: '100%',
});

const PickerBase = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
}));

const Separator = styled('div')(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.h6.fontSize,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    marginTop: theme.spacing(-0.5),
    userSelect: 'none',
}));

const DEFAULT_TIME_VALUE: ParsedTimeValue = {
    hours: 0,
    minutes: 0,
};

export interface TimePickerProps extends Omit<
    TextFieldProps,
    'value' | 'onChange'
> {
    value?: string | null;
    onChange: (value: string) => void;
    /** Emit sanitized partial input as well as committed times for live drafts. */
    live?: boolean;
    adornmentPosition?: 'start' | 'end';
    toggleTabIndex?: number;
}

export const TimePicker = ({
    value,
    onChange,
    live = false,
    disabled,
    onBaseClick,
    onBlur,
    onFocus,
    onKeyDown,
    placeholder = 'HH:mm',
    adornmentPosition = 'end',
    toggleTabIndex,
    startAdornment,
    endAdornment,
    variantSx,
    inputSx,
    ...textFieldProps
}: TimePickerProps) => {
    const [open, setOpen] = React.useState(false);
    const [text, setText] = React.useState(value || '');
    const [focused, setFocused] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
    const [draftValue, setDraftValue] =
        React.useState<ParsedTimeValue>(DEFAULT_TIME_VALUE);
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const currentValue = value || '';
    const parsedValue = React.useMemo(
        () => parseTimeInput(currentValue),
        [currentValue]
    );
    const selectedTimeRef = React.useRef<ParsedTimeValue>(DEFAULT_TIME_VALUE);

    const hours = open ? draftValue.hours : (parsedValue?.hours ?? 0);
    const minutes = open ? draftValue.minutes : (parsedValue?.minutes ?? 0);

    React.useEffect(() => {
        if (!focused) {
            setText(currentValue);
        }
    }, [currentValue, focused]);

    React.useEffect(() => {
        const nextValue = parsedValue || DEFAULT_TIME_VALUE;

        selectedTimeRef.current = nextValue;

        if (!open) {
            setDraftValue(nextValue);
        }
    }, [open, parsedValue]);

    const emitTimeValue = React.useCallback(
        (nextHours: number, nextMinutes: number) => {
            const nextValue = formatTimeValue(nextHours, nextMinutes);

            selectedTimeRef.current = {
                hours: nextHours,
                minutes: nextMinutes,
            };
            setDraftValue(selectedTimeRef.current);
            setText(nextValue);

            onChange(nextValue);
        },
        [onChange]
    );

    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, []);

    const handleOpen = React.useCallback(() => {
        if (!disabled) {
            const nextValue =
                parseTimeInput(text) || parsedValue || DEFAULT_TIME_VALUE;

            selectedTimeRef.current = nextValue;
            setDraftValue(nextValue);
            setOpen(true);

            window.requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        }
    }, [disabled, parsedValue, text]);

    const handleToggle = React.useCallback(() => {
        if (!disabled) {
            if (open) {
                handleClose();
                return;
            }

            handleOpen();
        }
    }, [disabled, handleClose, handleOpen, open]);

    const handleHourChange = React.useCallback(
        (nextHour: number) => {
            emitTimeValue(nextHour, selectedTimeRef.current.minutes);
        },
        [emitTimeValue]
    );

    const handleMinuteChange = React.useCallback(
        (nextMinute: number) => {
            emitTimeValue(selectedTimeRef.current.hours, nextMinute);
        },
        [emitTimeValue]
    );

    const commitInputValue = React.useCallback(
        (inputValue: string) => {
            const sanitizedValue = sanitizeTimeInput(inputValue).trim();

            if (sanitizedValue.length === 0) {
                setText('');
                setDraftValue(DEFAULT_TIME_VALUE);
                selectedTimeRef.current = DEFAULT_TIME_VALUE;

                if (currentValue) {
                    onChange('');
                }

                return;
            }

            const nextParts = parseTimeInput(sanitizedValue);
            if (!nextParts) {
                setText(sanitizedValue);
                return;
            }

            emitTimeValue(nextParts.hours, nextParts.minutes);
        },
        [currentValue, emitTimeValue, onChange]
    );

    const handleInputChange = React.useCallback(
        (nextValue: string) => {
            const sanitizedValue = sanitizeTimeInput(nextValue);
            const nextParts = parseTimeInput(sanitizedValue);
            if (live && /^\d{4}$/.test(sanitizedValue) && nextParts) {
                emitTimeValue(nextParts.hours, nextParts.minutes);
                return;
            }

            setText(sanitizedValue);
            if (live) {
                onChange(sanitizedValue);
            }

            if (open && nextParts) {
                selectedTimeRef.current = nextParts;
                setDraftValue(nextParts);
            }
        },
        [emitTimeValue, live, onChange, open]
    );

    const handleInputBlur = React.useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            onBlur?.(event);
            commitInputValue(text);
        },
        [commitInputValue, onBlur, text]
    );

    const handleInputFocus = React.useCallback(
        (event: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            onFocus?.(event);
        },
        [onFocus]
    );

    const toggleButton = (
        <ToggleButton
            type='button'
            tabIndex={toggleTabIndex}
            $disabled={disabled}
            disabled={disabled}
            onMouseDown={event => {
                event.preventDefault();
            }}
            onClick={event => {
                event.stopPropagation();
                handleToggle();
            }}>
            <Symbol symbol='schedule' size='sm' color='textSecondary' />
            {adornmentPosition === 'end' && (
                <Symbol
                    symbol={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    size='sm'
                    color='textSecondary'
                />
            )}
        </ToggleButton>
    );

    return (
        <Anchor ref={setAnchorEl}>
            <TextField
                {...textFieldProps}
                ref={inputRef}
                type='text'
                value={text}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onBaseClick={onBaseClick}
                onKeyDown={event => {
                    onKeyDown?.(event);

                    if (event.defaultPrevented) {
                        return;
                    }

                    if (event.key === 'Enter') {
                        commitInputValue(text);
                        return;
                    }

                    if (event.key === 'Escape') {
                        setText(currentValue);
                        handleClose();
                        event.preventDefault();
                    }
                }}
                disabled={disabled}
                placeholder={placeholder}
                autoComplete='off'
                spellCheck={false}
                inputMode='numeric'
                startAdornment={
                    adornmentPosition === 'start'
                        ? toggleButton
                        : startAdornment
                }
                endAdornment={
                    <>
                        {endAdornment}
                        {adornmentPosition === 'end' && toggleButton}
                    </>
                }
                variantSx={variantSx}
                inputSx={[
                    ...(Array.isArray(inputSx) ? inputSx : [inputSx || {}]),
                    {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontVariantNumeric: 'tabular-nums',
                    },
                ]}
            />
            <Popper
                open={open}
                anchorEl={anchorEl}
                transition
                placement='bottom'
                PaperProps={{
                    elevation: 8,
                }}
                handleClose={handleClose}
                hideOverflow>
                <PickerBase>
                    <TimePickerColumn
                        label='Hours'
                        value={hours}
                        total={24}
                        onChange={handleHourChange}
                    />
                    <Separator>:</Separator>
                    <TimePickerColumn
                        label='Minutes'
                        value={minutes}
                        total={60}
                        onChange={handleMinuteChange}
                    />
                </PickerBase>
            </Popper>
        </Anchor>
    );
};

export default TimePicker;
